/*
Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
  distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
  prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
  content, please contact the author for proper authorization and agreement.
  */

import React from 'react'
import clsx from 'clsx'
import { Language } from 'types/entities/Language'
import { languages as supportedLanguages } from 'helpers/config'
import { useTypedSelector } from 'helpers/reducers'
import SessionAction from 'store/session/SessionAction'
import i18next from 'i18next'
import { useAppDispatch } from 'helpers/store'
import CountryFlag from 'components/CountryIcons/CountryFlag'

type UiLanguageSwitcherProps = {
    className?: string
}

const languageToCountry: Record<string, string> = {
    en: 'GB',
    de: 'CH',
    fr: 'FR',
    it: 'IT',
    nl: 'NL',
    hu: 'HU',
    sk: 'SK',
    cs: 'CZ',
    cz: 'CZ',
}

const UiLanguageSwitcher: React.FC<UiLanguageSwitcherProps> = ({
    className,
}) => {
    const dispatch = useAppDispatch()
    const currentLanguageFromState = useTypedSelector(
        (state) => state.sessionState.language
    )

    const availableLanguages: Language[] =
        supportedLanguages && supportedLanguages.length > 0
            ? supportedLanguages
            : [
                  { id: 1, code: 'en', name: 'English' },
                  { id: 2, code: 'de', name: 'Deutsch' },
                  { id: 3, code: 'fr', name: 'Français' },
                  { id: 4, code: 'it', name: 'Italiano' },
                  { id: 5, code: 'nl', name: 'Nederlands' },
                  { id: 6, code: 'hu', name: 'Magyar' },
              ]

    const currentLanguageCode =
        currentLanguageFromState?.code || i18next.language || 'en'

    const handleCycleLanguage = () => {
        const currentIndex = availableLanguages.findIndex(
            (lang) => lang.code === currentLanguageCode
        )
        const nextIndex = (currentIndex + 1) % availableLanguages.length
        const nextLanguage = availableLanguages[nextIndex]
        dispatch(SessionAction.setLanguage(nextLanguage))
    }

    const countryCode =
        languageToCountry[currentLanguageCode.toLowerCase()] ||
        currentLanguageCode.toUpperCase()

    return (
        <button
            type="button"
            onClick={handleCycleLanguage}
            className={clsx(
                'UiLanguageSwitcher',
                'p-1.5 rounded-lg transition-colors overflow-hidden',
                'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800',
                'text-gray-800 dark:text-gray-100',
                className
            )}
            title={
                availableLanguages.find((l) => l.code === currentLanguageCode)
                    ?.name || currentLanguageCode.toUpperCase()
            }
        >
            <CountryFlag
                countryCode={countryCode}
                className={clsx('w-6 h-4 border-none shadow-sm rounded-sm')}
            />
        </button>
    )
}

export default UiLanguageSwitcher
